/* ============================================================
   o kama sona — pronouncing toki pona out loud
   ------------------------------------------------------------
   Toki pona has a tiny, phonetic sound system: five Spanish-style
   vowels (a e i o u) and a handful of consonants (p t k s m n l j w).
   Rather than record every word, we lean on the browser's built-in
   speech synthesis with a Spanish voice — Spanish vowels line up
   almost exactly with toki pona's.

   Two letters need transliterating first, because Spanish spells
   those sounds differently than toki pona does:
     j  -> hi/y toki pona "j" is the English "y" sound (/j/), but a
                Spanish "j" is a harsh throat-h. Between vowels a
                Spanish "y" is a soft glide, so "pimeja" -> "pimeya".
                At the START of a word, though, Spanish hardens "y"
                into an affricate (almost English "ch"/"j" — it begins
                with a t-like stop), which is why "yan" came out
                sounding like "chan". Spelling it "hi"+vowel keeps it
                a pure glide, the way "hielo" is /jelo/: "jan" ->
                "hian", "jelo" -> "hielo" (a real word, conveniently).
     w  -> hu   Spanish has no letter "w"; "hu" before a vowel is
                pronounced /w/ (as in "hueso"). "wan" -> "huan".

   Stress needs forcing only for LONG words: toki pona always
   stresses the FIRST syllable, and Spanish defaults to the
   penultimate — which IS the first syllable for one- and two-syllable
   words (every toki pona word ends in a vowel or n, so the default
   always applies). Only words of three or more syllables would come
   out wrong ("pimeja" as pi-ME-ja); those get an acute accent on the
   first syllabic vowel: "pimeja" -> "pímeya". Everything else stays
   accent-free on purpose — devices without a real Spanish voice can
   fall back to engines that spell accented letters out loud ("o
   acute, n, a"), so the fewer accents we emit, the better.

   When the device turns out to have NO Spanish voice at all, the
   Spanish spellings are themselves the problem: an English voice
   reads "huile" (wile) as something like "HYOO-ile". In that case we
   switch to an English-oriented phonetic respelling — toki pona
   syllables are strictly (C)V(n), so each maps to an English-safe
   chunk: "wile" -> "wee-leh", "ona" -> "oh-nah", "jan" -> "yahn".
   Rougher vowels than Spanish, but every word survives.

   Everything here degrades gracefully: if the Web Speech API or a
   Spanish voice is missing, `speechAvailable` is false and callers
   simply hide the speaker button.
   ============================================================ */

const synth =
  typeof window !== "undefined" && window.speechSynthesis
    ? window.speechSynthesis
    : null;

export const speechAvailable = !!synth;

const ACCENT = { a: "á", e: "é", i: "í", o: "ó", u: "ú" };
const VOWELS = "aeiou";

// Accent the first syllabic vowel of a 3+-syllable word so Spanish TTS
// stresses the first syllable (toki pona's invariable stress). Shorter
// words are correct by default — Spanish stresses the penultimate, which
// is the first syllable of a two-syllable word — and staying accent-free
// keeps fallback engines from spelling the accent out loud.
// Transliterated glides are not syllables of their own and never count
// as (or take) the accent: the "u" of a "hu"+vowel cluster (from w), and
// an "i" directly before another vowel (from word-initial j — toki pona
// has no vowel-vowel sequences, so any i+vowel here came from j).
const stressFirstSyllable = (word) => {
  const isVowel = (c) => !!c && VOWELS.includes(c);
  const nuclei = [];
  for (let i = 0; i < word.length; i++) {
    const c = word[i];
    if (!isVowel(c)) continue;
    const glide =
      (c === "u" && word[i - 1] === "h" && isVowel(word[i + 1])) ||
      (c === "i" && isVowel(word[i + 1]));
    if (glide) continue;
    nuclei.push(i);
  }
  if (nuclei.length < 3) return word;
  const p = nuclei[0];
  return word.slice(0, p) + ACCENT[word[p]] + word.slice(p + 1);
};

// A transliteration can collide with a real word of ANOTHER language that
// multilingual TTS lexicons special-case: "huile" (wile) is French for oil
// and gets read French-style ("weel") instead of by Spanish letter rules.
// An acute accent breaks the lexicon match without changing the Spanish
// reading — "huíle" is /ˈwile/ either way. The only known collision.
const RESPELL = { huile: "huíle" };

// Map toki pona spelling onto Spanish spelling that sounds the same,
// exported for tests.
export const toSpanishSpelling = (text) =>
  text
    .toLowerCase()
    .replace(/\bj/g, "hi") // word-initial: glide "hi" (hielo), never affricate "y"
    .replace(/j/g, "y") // between vowels "y" stays a soft glide
    .replace(/w/g, "hu")
    .split(" ")
    .map((word) => RESPELL[word] || stressFirstSyllable(word))
    .join(" ");

// Voices populate asynchronously in most browsers; cache the best
// Spanish match once it's available.
let spanishVoice = null;

// Rank Spanish voices by how good they tend to sound. The neural/cloud
// voices (Edge "Natural", Chrome's Google voices) render consonants far
// more cleanly than the tiny local formant engines, which is where most
// of the harsh artifacts come from. European Spanish still edges out
// other dialects (its vowels sit closest to toki pona's).
const scoreVoice = (v) => {
  if (!/^es([-_]|$)/i.test(v.lang)) return -1;
  let score = /^es[-_]ES/i.test(v.lang) ? 2 : 1;
  if (/natural|neural|premium|enhanced/i.test(v.name)) score += 8;
  if (/google/i.test(v.name)) score += 4;
  if (v.localService === false) score += 2; // cloud voices
  return score;
};

const pickSpanishVoice = () => {
  if (!synth) return null;
  const voices = synth.getVoices();
  if (!voices.length) return null;
  let best = null;
  let bestScore = 0;
  for (const v of voices) {
    const s = scoreVoice(v);
    if (s > bestScore) {
      best = v;
      bestScore = s;
    }
  }
  // No Spanish voice at all -> null (the engine falls back to its
  // default for the es-ES lang tag).
  spanishVoice = best;
  return spanishVoice;
};

if (synth) {
  pickSpanishVoice();
  // Re-resolve when the list finishes loading.
  if (typeof synth.addEventListener === "function") {
    synth.addEventListener("voiceschanged", pickSpanishVoice);
  } else {
    synth.onvoiceschanged = pickSpanishVoice;
  }
}

// Fold accented vowels back to plain ones ("óna" -> "ona"). Used when the
// voice list hasn't loaded yet and the engine handling es-ES is unknown —
// it may not know the accented letters and spell them out loud.
export const stripAccents = (text) =>
  text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// English-oriented phonetic respelling, for devices whose voice list has
// loaded and contains NO Spanish voice: whatever voice speaks there would
// mangle the Spanish spellings ("huile" for wile), so spell each toki pona
// syllable — always (C)V(n) — as an English-safe chunk instead:
// "wile" -> "wee-leh", "tenpo" -> "tehn-poh", "jan" -> "yahn".
const EN_VOWEL = { a: "ah", e: "eh", i: "ee", o: "oh", u: "oo" };
export const toEnglishRespelling = (text) =>
  text
    .toLowerCase()
    .split(" ")
    .map((word) =>
      (word.match(/[jklmnpstw]?[aeiou](?:n(?![aeiou]))?/g) || [word])
        .map((syl) => {
          const m = syl.match(/^([jklmnpstw]?)([aeiou])(n?)$/);
          if (!m) return syl;
          return (m[1] === "j" ? "y" : m[1]) + EN_VOWEL[m[2]] + m[3];
        })
        .join("-")
    )
    .join(" ");

let pendingSpeak = null;

/**
 * Speak a toki pona word or phrase out loud using a Spanish voice.
 * No-op (returns false) when speech isn't available.
 */
export const speakTokiPona = (text) => {
  if (!synth || !text) return false;
  // Cancel anything mid-flight so rapid taps don't queue up.
  if (pendingSpeak) clearTimeout(pendingSpeak);
  synth.cancel();
  const voice = spanishVoice || pickSpanishVoice();
  let spoken;
  let lang = "es-ES";
  if (voice) {
    spoken = toSpanishSpelling(text);
  } else if (synth.getVoices().length) {
    // The voice list is loaded and Spanish just isn't on it — Spanish
    // spellings would come out mangled, so speak English-style instead.
    spoken = toEnglishRespelling(text);
    lang = "en-US";
  } else {
    // Voice list not loaded yet: es-ES may still resolve inside the
    // engine, so keep the Spanish spelling minus the risky accents.
    spoken = stripAccents(toSpanishSpelling(text));
  }
  const utter = new SpeechSynthesisUtterance(spoken);
  utter.lang = lang;
  utter.rate = 0.85; // a touch slow — clearer for learners
  if (voice) utter.voice = voice;
  // Chrome sometimes swallows or clips the first consonant when speak()
  // follows cancel() in the same tick — give the engine a beat to reset.
  pendingSpeak = setTimeout(() => {
    pendingSpeak = null;
    synth.speak(utter);
  }, 60);
  return true;
};
